import { useState } from 'react';
import { ChromePicker } from 'react-color';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconEdit } from '@components/icons/edit';
import { IconLabel } from '@components/icons/label';
import { IconReturn } from '@components/icons/return';
import { IconTrash } from '@components/icons/trash';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { PageContainers } from '@components/page-containers';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Tag } from '@components/tag';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { useScopedTranslate } from '@locale/translate';

import { useCreateJobLabel, useEditJobLabel, useJobLabels } from 'api/labels/hooks';
import { JobLabel } from 'api/labels/requests';
import { PageContent } from 'components/Page';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';

import { ColorCircle } from './labels';
import locale from './locale.json';

type JobLabelDetailProps = {
  label: JobLabel;
  setIsEdit: (isEdit: boolean) => void;
};

const JobLabelDetail = ({ label, setIsEdit }: JobLabelDetailProps) => {
  const { t } = useScopedTranslate(locale);
  const { showModal } = useModal();
  const { hasRolePermission } = usePermissions();

  return (
    <Card space={2}>
      <Stack orientation="horizontal" justify="space-between">
        <Stack space={4} orientation="horizontal">
          <IconButton icon={<ColorCircle color={label.color} />} variant="ghost-purple" />
          <Stack>
            <Text font="semibold14" text={label.title} />
            <Text font="regular14" text={t('assignedNr', (label.numJobs ?? 0).toString())} />
          </Stack>
        </Stack>
        <Stack space={2} orientation="horizontal">
          {hasRolePermission('joblabels:edit') && (
            <IconButton
              icon={<IconEdit />}
              variant="ghost-purple"
              onClick={() => setIsEdit(true)}
            />
          )}
          {hasRolePermission('joblabels:delete') && (
            <IconButton
              icon={<IconTrash />}
              variant="ghost-purple"
              onClick={() => showModal('DELETE_JOB_LABEL', { labelId: label.id })}
            />
          )}
        </Stack>
      </Stack>
    </Card>
  );
};

type JobLabelEditProps = {
  label?: JobLabel;
  setIsEdit: (isEdit: boolean) => void;
  inlineEdit?: boolean;
  onCreateSuccess?: (label: string, value: number, color: string) => void;
};

export const JobLabelEdit = ({
  label,
  setIsEdit,
  inlineEdit = true,
  onCreateSuccess,
}: JobLabelEditProps) => {
  const { t } = useScopedTranslate(locale);
  const createMutation = useCreateJobLabel();
  const editMutation = useEditJobLabel();

  const colors = [
    { color: '#3C3C64', label: t('purple') },
    { color: '#05BA8C', label: t('green') },
    { color: '#005FFF', label: t('blue') },
    { color: '#E1943E', label: t('orange') },
    { color: '#DC3232', label: t('red') },
  ];
  const [showCustom, setShowCustom] = useState(false);
  const [color, setColor] = useState(label?.color ?? '#3C3C64');
  const [customColor, setCustomColor] = useState('#000000');
  const [title, setTitle] = useState(label?.title ?? '');

  const onSuccess = async (id?: number) => {
    setIsEdit(false);
    if (id !== undefined) {
      onCreateSuccess?.(title, id, color);
    }
  };

  const handleSave = () => {
    if (label && label.id) {
      editMutation.mutate({ id: label.id, title: title, color: color, onSuccess });
    } else {
      createMutation.mutate({ title: title, color: color, onSuccess });
    }
  };

  const submitButtons = (
    <>
      <Button variant="secondary" text={t('cancel')} onClick={() => setIsEdit(false)} />
      <Button
        iconLeft={<IconReturn />}
        variant="primary-blue"
        text={t('save')}
        onClick={() => handleSave()}
        isDisabled={!title || !color || editMutation.isLoading || createMutation.isLoading}
      />
    </>
  );

  return (
    <>
      <Card space={4} variant="gray">
        <Stack space={4}>
          <Stack space={4} orientation="horizontal" justify="space-between">
            <Stack space={4} orientation="horizontal" style={{ flexGrow: 1 }}>
              <Stack space={4}>
                <Menu
                  placement="bottom-start"
                  buttonProps={{
                    variant: 'secondary',
                    iconRight: <ColorCircle color={color} />,
                  }}
                >
                  {showCustom ? (
                    <Stack space={6}>
                      <Menu.Item
                        icon={<IconArrowLeft />}
                        label={t('back')}
                        onSelect={(event) => {
                          setShowCustom(false);
                          event.preventDefault();
                        }}
                      />
                      <ChromePicker
                        color={customColor}
                        disableAlpha
                        onChange={(value) => {
                          setCustomColor(value.hex);
                        }}
                      />
                      <Stack orientation="horizontal" justify="flex-end">
                        <Button
                          onClick={() => {
                            setColor(customColor);
                            setShowCustom(false);
                          }}
                          text={t('applyColor')}
                        />
                      </Stack>
                    </Stack>
                  ) : (
                    <>
                      <Menu.Item
                        icon={<IconAddPlus />}
                        label={t('customColor')}
                        onSelect={(event) => {
                          setShowCustom(true);
                          event.preventDefault();
                        }}
                      />
                      {colors.map((colorOption) => (
                        <Menu.Item
                          key={colorOption.color}
                          icon={<ColorCircle color={colorOption.color} />}
                          label={colorOption.label}
                          onSelect={() => setColor(colorOption.color)}
                        />
                      ))}
                    </>
                  )}
                </Menu>
              </Stack>
              <Stack space={4} style={{ flexGrow: 1 }}>
                <TextField
                  label="Label name"
                  hideLabel
                  onChange={(title) => setTitle(title)}
                  placeholder={t('writeLabelName')}
                  value={title}
                />
              </Stack>
            </Stack>
            {inlineEdit && (
              <Stack space={2} orientation="horizontal">
                {submitButtons}
              </Stack>
            )}
          </Stack>
          <Stack orientation="horizontal">
            <Tag size="small" text={title || t('newLabel')} color={color} variant="customColor" />
          </Stack>
        </Stack>
      </Card>
      {!inlineEdit && (
        <Stack orientation="horizontal" space={4} justify="flex-end">
          {submitButtons}
        </Stack>
      )}
    </>
  );
};

type JobLabelProps = {
  label: JobLabel;
};
const JobLabelItem = ({ label }: JobLabelProps) => {
  const [isEdit, setIsEdit] = useState(false);
  return (
    <>
      {isEdit ? (
        <JobLabelEdit label={label} setIsEdit={setIsEdit} />
      ) : (
        <JobLabelDetail label={label} setIsEdit={setIsEdit} />
      )}
    </>
  );
};

export const JobLabels = () => {
  const { t } = useScopedTranslate(locale);
  const { hasRolePermission } = usePermissions();
  const [isCreate, setIsCreate] = useState(false);
  const jobLabelQuery = useJobLabels();
  const jobLabels = jobLabelQuery.data ?? [];

  if (jobLabelQuery.isLoading) return <Spinner />;

  return (
    <PageContent>
      <PageContainers.MiddleColumnPage>
        <Stack space={4}>
          <Stack space={6}>
            <Stack space={2}>
              <Text font="bold18" text={t('jobLabels')} />
              <Text text={t('manageJobLabels')} />
            </Stack>
            <Separator />
          </Stack>
          <Stack space={2}>
            {hasRolePermission('joblabels:add') && (
              <Stack orientation="horizontal" justify="flex-end">
                <Button
                  text={t('add')}
                  iconLeft={<IconAddPlus />}
                  onClick={() => setIsCreate(true)}
                />
              </Stack>
            )}
            {isCreate ? <JobLabelEdit setIsEdit={setIsCreate} /> : null}
            {jobLabels.length === 0 && !isCreate ? (
              <Card variant="gray" space={6}>
                <Stack space={4} orientation="horizontal">
                  <IconLabel />
                  <Text text={t('noJobLabels')} />
                </Stack>
              </Card>
            ) : (
              jobLabels.map((label) => <JobLabelItem key={label.id} label={label} />)
            )}
          </Stack>
        </Stack>
      </PageContainers.MiddleColumnPage>
    </PageContent>
  );
};
